/**
 * Canonical reference helpers — version-pinning utilities used across
 * versioned domain types (PlanDefinition / ActivityDefinition).
 */

interface Versioned {
  url?: string;
  version?: string;
}

/**
 * Build the canonical reference string downstream resources pin to. Returns
 * `url|version` when both are stamped, bare `url` for drafts (version not
 * yet stamped), or undefined when neither is set.
 */
export function toCanonicalRef(v: Versioned): string | undefined {
  if (v.url && v.version) return `${v.url}|${v.version}`;
  return v.url ?? undefined;
}

export interface ParsedCanonical {
  url: string;
  version?: string;
}

/**
 * A canonical that names an exact revision. Anything MATERIALIZED from a
 * definition — a protocol's order set, a questionnaire's response — pins
 * `url|version`, because the definition it was realized from is a fact about
 * that instance and must not move when the catalog does. `ParsedCanonical`
 * stays version-optional: it is what a string parses to, and a draft's
 * canonical has no version to find.
 */
export interface VersionedCanonical {
  url: string;
  version: string;
}

/**
 * The trailing segment of a canonical url — the UUID it was minted with. Spelled
 * once because four call sites derived it inline, and a base url that ever gains
 * a path segment would have broken each of them separately.
 */
export function canonicalSegment(url: string): string {
  return url.slice(url.lastIndexOf("/") + 1);
}

/** A definition lineage, deliberately unpinned to any revision. */
export type CanonicalLineage = Brand<string, "CanonicalLineage">;

export const canonicalLineageSchema = z
  .string()
  .url()
  .refine((value) => !value.includes("|"), "A canonical lineage must be a bare URL")
  .transform((value) => value as CanonicalLineage);

export const versionedCanonicalSchema: z.ZodType<VersionedCanonical> = z.object({
  url: z.string().url(),
  version: z.string().min(1),
});

/**
 * `parseCanonicalRef` for a reference that must be version-pinned. Returns
 * undefined for an unversioned or empty ref — callers that require a pin
 * should refuse it rather than carry a canonical that can drift.
 */
export function parsePinnedCanonical(ref: string | undefined): VersionedCanonical | undefined {
  const parsed = parseCanonicalRef(ref);
  return parsed?.version ? { url: parsed.url, version: parsed.version } : undefined;
}

/**
 * Inverse of `toCanonicalRef`. Splits a canonical reference like
 * `https://mindforge.health/fhir/PlanDefinition/abc|1.2.0` into its URL and
 * version parts. Empty/missing version returns `undefined`. Returns
 * `undefined` for empty input.
 */
export function parseCanonicalRef(ref: string | undefined): ParsedCanonical | undefined {
  if (!ref) return undefined;
  const pipe = ref.indexOf("|");
  if (pipe < 0) return { url: ref };
  const url = ref.slice(0, pipe);
  const version = ref.slice(pipe + 1);
  if (!url) return undefined;
  return version ? { url, version } : { url };
}

import { z } from "zod";
import type { Brand } from "./brand";
